import React, { memo } from 'react'

import { Flag, Lock } from '@mui/icons-material'
import {
  Box,
  Chip,
  Container,
  Divider,
  Grid,
  Link,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'

import { APP_CONFIG } from '@/utils/constants'

const Footer = memo(() => {
  const theme = useTheme()

  const footerLinks = {
    operations: [
      { label: 'System Dashboard', href: '#' },
      { label: 'Service Health', href: '#' },
      { label: 'Incident Management', href: '#' },
      { label: 'Performance Analytics', href: '#' },
    ],
    resources: [
      { label: 'Documentation', href: '#' },
      { label: 'API Reference', href: '#' },
      { label: 'Training Center', href: '#' },
      { label: 'Best Practices', href: '#' },
    ],
    support: [
      { label: 'Help Center', href: '#' },
      { label: 'Submit Ticket', href: '#' },
      { label: 'Contact IT', href: '#' },
      { label: 'FAQs', href: '#' },
    ],
    legal: [
      { label: 'Privacy Policy', href: '#' },
      { label: 'Terms of Service', href: '#' },
      { label: 'Security Policy', href: '#' },
      { label: 'Compliance Reports', href: '#' },
    ],
  }

  return (
    <Box
      component="footer"
      sx={{
        mt: 8,
        py: 6,
        bgcolor: alpha(theme.palette.background.paper, 0.5),
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      }}
    >
      <Container maxWidth="xl">
        <Grid container spacing={6}>
          {/* Organization Info */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Stack spacing={3}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: theme.shadows[3],
                  }}
                >
                  <Flag sx={{ fontSize: 28, color: 'white' }} />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={800}>
                    {APP_CONFIG.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Federal Government Internal Portal
                  </Typography>
                </Box>
              </Stack>

              <Typography variant="body2" color="text.secondary" maxWidth={350}>
                Building a secure, inclusive digital identity infrastructure for the Federal
                Democratic Republic of Ethiopia.
              </Typography>

              <Stack direction="row" spacing={1}>
                <Chip label="ISO 27001" size="small" variant="outlined" />
                <Chip label="GDPR" size="small" variant="outlined" />
                <Chip label="SOC 2" size="small" variant="outlined" />
              </Stack>

              <Box>
                <Typography variant="subtitle2" fontWeight={700} color="error.main" gutterBottom>
                  24/7 Security Hotline
                </Typography>
                <Typography variant="h6" fontWeight={700}>
                  {APP_CONFIG.supportPhone}
                </Typography>
              </Box>
            </Stack>
          </Grid>

          {/* Links Grid */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Grid container spacing={4}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                  Operations
                </Typography>
                <Stack spacing={1.5}>
                  {footerLinks.operations.map((link) => (
                    <Link
                      key={link.label}
                      href={link.href}
                      variant="body2"
                      color="text.secondary"
                      underline="hover"
                      sx={{
                        transition: 'color 0.2s ease',
                        '&:hover': {
                          color: theme.palette.primary.main,
                        },
                      }}
                    >
                      {link.label}
                    </Link>
                  ))}
                </Stack>
              </Grid>

              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                  Resources
                </Typography>
                <Stack spacing={1.5}>
                  {footerLinks.resources.map((link) => (
                    <Link
                      key={link.label}
                      href={link.href}
                      variant="body2"
                      color="text.secondary"
                      underline="hover"
                      sx={{
                        transition: 'color 0.2s ease',
                        '&:hover': {
                          color: theme.palette.primary.main,
                        },
                      }}
                    >
                      {link.label}
                    </Link>
                  ))}
                </Stack>
              </Grid>

              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                  Support
                </Typography>
                <Stack spacing={1.5}>
                  {footerLinks.support.map((link) => (
                    <Link
                      key={link.label}
                      href={link.href}
                      variant="body2"
                      color="text.secondary"
                      underline="hover"
                      sx={{
                        transition: 'color 0.2s ease',
                        '&:hover': {
                          color: theme.palette.primary.main,
                        },
                      }}
                    >
                      {link.label}
                    </Link>
                  ))}
                </Stack>
              </Grid>

              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                  Legal & Compliance
                </Typography>
                <Stack spacing={1.5}>
                  {footerLinks.legal.map((link) => (
                    <Link
                      key={link.label}
                      href={link.href}
                      variant="body2"
                      color="text.secondary"
                      underline="hover"
                      sx={{
                        transition: 'color 0.2s ease',
                        '&:hover': {
                          color: theme.palette.primary.main,
                        },
                      }}
                    >
                      {link.label}
                    </Link>
                  ))}
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        {/* Bottom Bar */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
        >
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} {APP_CONFIG.organization}. All rights reserved.
          </Typography>
          <Stack direction="row" spacing={3} alignItems="center">
            <Chip
              icon={<Lock />}
              label="Restricted Access"
              color="error"
              size="small"
              variant="outlined"
            />
            <Typography variant="caption" color="text.secondary">
              All activities are monitored and logged for security compliance
            </Typography>
          </Stack>
        </Stack>
      </Container>
    </Box>
  )
})

Footer.displayName = 'Footer'

export default Footer
